import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'

import ApiServiceV1 from '../api/adminApi/AdminApiV1'
import { payMethodEnum } from '../enums/payMethodEnum'
import { numberToCurrency } from '../mixins/numberToCurrency'
import BrowserService from '../services/browserService'
import { CartService } from '../services/cartService'
import type { ICartStore } from './Interfaces/ICartStore'
import { useModalStore } from '@/stores/ModalStore'
import type { Distributor } from '@/types/distributor'
import type { Order } from '@/types/order'
import { isAxiosError } from 'axios'

export const useCartStore = defineStore('cartStore', {
  state: (): ICartStore => ({
    cart: new CartService().getCartObject(),
    order: null,
    currentStep: 0,
    stepperBackgroundColor: null,
    orderAsGift: false,
    contactData: {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      phonePrefix: undefined,
      phone: undefined,
    },
    shippingAddress: {
      company: undefined,
      street: undefined,
      streetNumber: undefined,
      flatNumber: undefined,
      city: undefined,
      zip: undefined,
      country: undefined,
      note: undefined,
      customer_nr: undefined,
    },
    billingContactData: {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      phonePrefix: undefined,
      phone: undefined,
    },
    isShippingAddressEdited: false,
    isBillingAddressEdited: false,
    billingAddress: {
      company: undefined,
      street: undefined,
      streetNumber: undefined,
      city: undefined,
      zip: undefined,
      country: undefined,
    },
    vatData: {
      ic: undefined,
      dic: undefined,
    },
    deliveryInformation: {
      branch_id: undefined,
      branch_name: undefined,
      branch_street: undefined,
      branch_city: undefined,
      branch_zip: undefined,
      branch_country: undefined,
    },
    errors: null,
    transports: null,
    payments: null,
  }),

  actions: {
    resetStepper() {
      this.currentStep = 0
    },
    goToNextStep() {
      this.currentStep += 1
    },
    goToPrevStep() {
      this.currentStep -= 1
    },
    setOrder(order: Order) {
      this.order = order
      localStorage.setItem('last_order_hash', order.hash)
    },
    setStepperBackgroundColor(color: string) {
      this.stepperBackgroundColor = color
    },
    setSelectedBillingAddress(value: boolean) {
      this.cart.selectedBillingAddress = this.cart.orderAsGift ? true : value
    },
    setSelectedBuyingForCompany(value: boolean) {
      this.cart.selectedBuyingForCompany = value
    },
    setGiftMessage(value: string) {
      this.cart.giftMessage = value
    },
    setErrors(errors: string[]) {
      this.errors = errors
    },
    clearErrors() {
      this.errors = null
    },
    setAddressAsEdited(params: {kind: string, value: boolean}) {
      if (params.kind === 'shipping') {
        this.isShippingAddressEdited = params.value
      } else if (params.kind === 'billing') {
        this.isBillingAddressEdited = params.value
      }
    },
    async syncCart() {
      if (!localStorage.cart_token) {
        return
      }

      try {
        const { data } = await ApiServiceV1.get('cart')

        this.vatData = {
          ic: data.cart.ic,
          dic: data.cart.dic,
        }

        this.cart = new CartService().getCartObject(data.cart)
      } catch {
        localStorage.removeItem("cart_token")
      }

    },
    setDeliveryInformation(data: Distributor | undefined) {
      if (data) {
        this.cart.deliveryInformation = {
          branch_id: data.id,
          branch_name: data.name,
          branch_street: data.street,
          branch_city: data.city,
          branch_zip: data.zip.replace(/\s/gu, ''),
        }
      }
    },
    resetCart() {
      this.cart = new CartService().getCartObject()
    },
    clearCart() {
      localStorage.removeItem('cart_token')
      this.cart = new CartService().getCartObject()
    },
    async handleCartToken(cartToken: string) {
      if (localStorage.cart_token === cartToken) {
        await this.syncCart()
      } else {
        localStorage.cart_token = cartToken
        await this.setLocalCart(cartToken)
      }
    },
    async setLocalCart(cartToken: string) {
      if (cartToken !== undefined) {
        const localCart = jwtDecode<{ cartID: number }>(cartToken)
        if (localCart !== undefined && localCart.cartID > 0 && this.cart.id !== localCart.cartID) {
          this.cart.id = localCart.cartID
          await this.syncCart()
        }
      }
    },
    resetCartProducts() {
      if (this.cart.cartProducts && this.cart.cartProducts.length > 0) {
        this.cart.cartProducts.splice(0, this.cart.cartProducts.length)
      }
    },
    updateMixFavorite(data: {mixID: number, isFavorite: boolean}) {
      const index = this.cart.cartMixes!.findIndex((item) => item.mixID === data.mixID)
      if (this.cart.cartMixes![index]) {
        this.cart.cartMixes![index].isFavorite = data.isFavorite
      }
    },
    async saveDeliveryData() {
      this.clearErrors()

      try {
        const response = await ApiServiceV1.put('cart/save-delivery-data', {
          order: {
            shipping: {
              firstname: this.contactData.firstName ?? null,
              surname: this.contactData.lastName ?? null,
              company: this.shippingAddress.company ?? null,
              phoneprefix: this.contactData.phonePrefix ?? null,
              phone: this.contactData.phone ? (this.contactData.phone).replace(/\s/g, '') : null,
              email: this.contactData.email ?? null,
              street: this.shippingAddress.street ?? null,
              city: this.shippingAddress.city ?? null,
              zip: this.shippingAddress.zip ? (this.shippingAddress.zip).replace(/\s/g, '') : null,
              country: this.shippingAddress.country ?? null,
              apartment: this.shippingAddress.note ?? null,
              customer_nr: this.shippingAddress.customer_nr ?? null,
            },
            billing: {
              firstname: this.billingContactData.firstName ?? null,
              surname: this.billingContactData.lastName ?? null,
              company: this.billingAddress.company ?? null,
              phoneprefix: this.billingContactData.phonePrefix ?? null,
              phone: this.billingContactData.phone ? (this.billingContactData.phone).replace(/\s/g, '') : null,
              email: this.billingContactData.email ?? null,
              street: this.billingAddress.street ?? null,
              city: this.billingAddress.city ?? null,
              zip: this.billingAddress.zip ? (this.billingAddress.zip).replace(/\s/g, '') : null,
              country: this.billingAddress.country ?? null,
            },
            vat_data: this.vatData ?? null,
            cart: {
              has_billing_address: this.cart.selectedBillingAddress ?? null,
              order_as_gift: this.cart.orderAsGift ?? null,
              gift_message: this.cart.giftMessage ?? null,
            },
          },
        })

        await this.handleCartToken(response.data.cart_token)
        return response
      } catch (error) {
        if (isAxiosError(error)) {
          this.setErrors(error.response!.data.errors)
        }
        throw error
      }
    },
    async createOrder(params: {
      celiac: boolean,
      comment: string,
      is_mobile_app_order: boolean,
    }) {
      this.clearErrors()

      try {
        const response = await ApiServiceV1.post('orders', params)
        this.clearCart()
        return response
      } catch (error) {
        if (isAxiosError(error)) {
          this.setErrors([error.response!.data.msg])
        }
        throw error
      }
    },
    async loadTransports() {
      const { data } = await ApiServiceV1.get('cart_transports')
      this.transports = data.transports
    },
    async loadPayments() {
      const { data } = await ApiServiceV1.get('cart_payments')
      if (!BrowserService.supportApplePay()) {
        data.payments = data.payments.filter((payment: {specialKind: string}) => payment.specialKind !== payMethodEnum.APPLE_PAY)
      }
      this.payments = data
    },
    async loadPaymentsAndTransports(cartToken: string) {
      await this.handleCartToken(cartToken)
      await this.loadTransports()
      await this.loadPayments()
    },
    async paymentSelected(uuid: string) {
      const { data } = await ApiServiceV1.put('cart_payments/set-payment', { uuid })
      await this.loadPaymentsAndTransports(data.cart_token)
    },
    async transportSelected(uuid: string) {
      const { data } = await ApiServiceV1.put('cart_transports/set-transport', { uuid })
      await this.loadPaymentsAndTransports(data.cart_token)
    },
    async orderAsGiftToggled(value: boolean) {
      const { data } = await ApiServiceV1.put('cart/set-order-as-gift', { order_as_gift: value })
      await this.loadPaymentsAndTransports(data.cart_token)
    },
    async buyingForCompanyToggled(value: boolean) {
      await ApiServiceV1.put('cart/set-buying-for-company', { buying_for_company: value })
    },
    async addOrderGift(productID: string) {
      const { openModal } = useModalStore()
      try {
        const { data } = await ApiServiceV1.post('cart_order_gifts', { product_id: productID })
        await this.handleCartToken(data.cart_token)
      } catch (error) {
        if (isAxiosError(error)) {
          this.setErrors([error.response!.data.msg])
          openModal({ id: 'modal-alert', title: error.response!.data.msg })
        }
      }
    },
    async removeOrderGift(itemID: number) {
      const { openModal } = useModalStore()
      try {
        const { data } = await ApiServiceV1.delete(`cart_order_gifts/${itemID}`)
        await this.handleCartToken(data.cart_token)
      } catch (error) {
        if (isAxiosError(error)) {
          openModal({ id: 'modal-alert', title: error.response!.data.msg })
        }
      }
    },
  },

  getters: {
    getStore: (state) => state,
    getCart: (state) => state.cart,
    getCartOnlyVirtualProducts: (state) => state.cart.onlyVirtualProducts,
    getCartPrice: (state) => numberToCurrency(state.cart.productPrice, state.cart.currency),
    getOrderPrice: (state) => numberToCurrency(state.order?.totalPrice, state.order?.currency),
    getOrder: (state) => state.order,
    getCartProducts: (state) => state.cart.cartProducts ?? [],
    getCartMixes: (state) => state.cart.cartMixes,
    isOrderAsGift: (state) => state.cart.orderAsGift,
    getProductQuantity: (state) => (productId: number) => {
      return state.cart?.cartProducts?.find((cartProduct) => cartProduct.productID === productId)?.quantity ?? 0
    },
    getProductInCart: (state) => (productId: number) => state.cart?.cartProducts?.find((item) => item.productID === productId),
    isCartNotEmpty: (state) => (state.cart.cartProducts?.length ?? 0) > 0 || (state.cart.cartMixes?.length ?? 0) > 0,
    getTotalItemsInCart: (state) => {
      let totalProducts = 0
      if (state.cart.cartProducts) {
        for (const cartProduct of state.cart.cartProducts) {
          totalProducts += cartProduct.quantity
        }
      }
      let totalMixes = 0
      if (state.cart.cartMixes) {
        for (const cartMix of state.cart.cartMixes) {
          totalMixes += cartMix.quantity
        }
      }
      return totalProducts + totalMixes
    },
    getStepperBackgroundColor: (state) => state.stepperBackgroundColor,
    getAppliedVouchers: (state) => state.cart.appliedVouchers,
    getDiscountItems: (state) => state.cart.discountItems,
    getGiftMessage: (state) => state.cart.giftMessage,
    getDeliveryInformation: (state) => state.cart.deliveryInformation,
    getShippingAddress: (state) => state.shippingAddress,
    getBillingAddress: (state) => state.billingAddress,
    getContactData: (state) => state.contactData,
    getBillingContactData: (state) => state.billingContactData,
    getErrors: (state) => state.errors,
    getCurrentStep: (state) => state.currentStep,
    getVatData: (state) => state.vatData,
    getPayments: (state) => state.payments,
    getTransports: (state) => state.transports,
    hasAppliedVoucher: (state) => (state.cart.appliedVouchers?.length ?? 0) > 0,
    hasDiscount: (state) => (state.cart.discountItems?.length ?? 0) > 0,
    getSelectedTransport: (state) => state?.cart.selectedTransport,
    getBranchName: (state) => state.cart?.deliveryInformation?.branch_name,
    hasFreeTransport: (state) => state.cart?.hasFreeTransport,
    inPlusClub: (state) => state.cart?.inPlusClub,
    orderGift: (state) => state.cart?.orderGift,
  },
})
